import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { OrgRole } from "services/orbac/types";
import { useUsers } from "services/users/useUsers";

import { TextInput } from "components/TextInput/TextInput";
import { Step } from "components/Wizard/Step/Step";

import {
  NameStepType,
  OrganizationRolesWizardStep,
  RolesWizardStep,
} from "../types";
import { Fields, schema } from "./schema";

import styles from "./styles.module.scss";

export type Props = {
  initialData: NameStepType;
  onClose: () => void;
  onSubmit: (data: Fields) => void;
  onFinished: (
    rolesData: any,
    selectedObjects: { grantee: string; principalType: string }[]
  ) => void;
  isLoading?: boolean;
  onPrevStep: () => void;
  activeStepIndex: number;
  disableNameChange?: boolean;
  validate?: (
    data: Fields,
    form: ReturnType<typeof useForm<Fields>>
  ) => boolean;
  roles: { roleName: string }[];
  steps: {
    id: OrganizationRolesWizardStep;
    title: string;
  }[];
  role?: OrgRole;
};

export const NameStep = (props: Props) => {
  const {
    onClose,
    activeStepIndex,
    onPrevStep,
    initialData,
    onSubmit,
    onFinished,
    steps,
    isLoading,
    disableNameChange,
    validate,
    roles,
    role,
  } = props;
  const { t } = useTranslation();
  const users = useUsers();

  const isLastStep = steps?.length && steps.length - 1 === activeStepIndex;

  const form = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: initialData,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = form;

  const body = (
    <div className={styles.rows}>
      <div className={styles.formControl}>
        <TextInput
          inputRootClassName={styles.inputRoot}
          label={t("roles_wizard.name.role_name")}
          testId="role-name-field"
          {...register("name")}
          error={!!errors?.name}
          helperText={errors?.name?.message}
          required
          autoFocus
          showAsterisk
          disabled={disableNameChange}
        />
      </div>
    </div>
  );

  const onFormSubmit = (data: Fields) => {
    if (validate) {
      const isValid = validate(data, form);
      if (!isValid) {
        return;
      }
    }

    if (!validate) {
      const duplicatedRole =
        !disableNameChange &&
        data.name &&
        roles.find(role => role.roleName === data.name);

      const duplicatedUser =
        !disableNameChange &&
        data.name &&
        users.find(user => user.userName === data.name);

      if (duplicatedRole || duplicatedUser) {
        setError("name", {
          message: duplicatedRole
            ? t("roles_wizard.name.role_already_exists")
            : t("roles_wizard.name.user_already_exists"),
        });
        return;
      }
    }

    if (isLastStep) {
      onFinished(
        {
          [RolesWizardStep.name]: {
            name: data.name,
          },
        },
        []
      );
      return;
    }
    onSubmit(data);
  };

  const submitTextForLastStep = role
    ? t("roles_wizard.assign.save")
    : t("roles_wizard.assign.create");

  return (
    <Step
      title={t("roles_wizard.name.title")}
      subtitle={t("roles_wizard.name.subtitle")}
      body={body}
      onClose={onClose}
      onSubmit={handleSubmit(onFormSubmit)}
      activeStepIndex={activeStepIndex}
      onPrevStep={onPrevStep}
      isLoading={isLoading}
      titleTestId="roles-wizard-name-title"
      mainActionTitle={isLastStep ? submitTextForLastStep : ""}
    />
  );
};
